// Navigator Risk Autopilot - Use-case risk processing for Navigator assessments
// Uses Navigator services and Redux state (navigatorRisks, not explorerRisks or modelRisks)
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Card, Stack, Group, Text, Badge,
  RingProgress, Center, Box,
  Timeline, Divider, Alert, Button, Transition
} from '@mantine/core';
import {
  IconTarget, IconAnalyze, IconSearch,
  IconRefresh, IconShieldCheck, IconRocket,
  IconBrain, IconUsers, IconLock,
  IconAlertTriangle, IconCheck, IconHourglassHigh, IconChevronRight,
  IconScale
} from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
// Navigator Services
import {
  NavigatorDiscoveryService,
  NavigatorEvaluationService,
  NavigatorActionPlanningService,
  NavigatorResidualRiskService
} from './services';
// RTK slice actions
import {
  selectInheritedBuilderRisks,
  NAVIGATOR_RISK_AREAS
} from '../redux/slices/risksSlice';
import {
  setAreaStatus,
  updateAreaProgress,
  saveCheckpoint,
  markResumed,
  clearCheckpoint,
  selectResumeData
} from '../redux/slices/assessmentProgressSlice';
import {
  fetchRiskCardData,
  setAssessmentCheckpoint
} from '../redux/slices/assessmentsSlice';
// Selectors
import { createImpactCategoriesSelector } from '../redux/selectors/riskFormGenerator';
import { selectContextBridge } from '../redux/selectors/assessmentSelectors';

// Navigator-specific task definitions (use-case focus)
const NAVIGATOR_TASKS = [
  {
    key: 'analyzing risk area',
    label: 'Analyzing Risk Impact',
    icon: IconAnalyze,
    color: '#7c3aed',
    description: 'Analyzing use-case risk landscape and deployment context'
  },
  {
    key: 'researching risk sources',
    label: 'Researching Risk Sources',
    icon: IconSearch,
    color: '#339af0',
    description: 'Evidence extraction for deployment risk sources'
  },
  {
    key: 'researching root causes',
    label: 'Researching Root Causes',
    icon: IconTarget,
    color: '#fd7e14',
    description: 'Research causes with deployment impact analysis'
  },
  {
    key: 'preparing causes for RAG',
    label: 'Preparing Causes for RAG',
    icon: IconBrain,
    color: '#20c997',
    description: 'Extracting relevant causes for enhanced scenario generation'
  },
  {
    key: 'scenarios',
    label: 'Generating Risk Scenarios',
    icon: IconAlertTriangle,
    color: '#e03131',
    description: 'Creating use-case risk scenarios with RAG-enhanced analysis'
  },
  {
    key: 'evaluation',
    label: 'Evaluating Risk Priorities',
    icon: IconScale,
    color: '#9c36b5',
    description: 'Assessing risk priority using AI'
  },
  {
    key: 'actions',
    label: 'Planning Actions',
    icon: IconRocket,
    color: '#37b24d',
    description: 'Developing mitigation strategies'
  },
  {
    key: 'residual',
    label: 'Calculating Residual Risk',
    icon: IconShieldCheck,
    color: '#f76707',
    description: 'Determining remaining risk after controls'
  }
];

// Navigator area labels (use-case focus)
const NAVIGATOR_AREA_LABELS = {
  userSafety: { label: 'User Safety', icon: IconShieldCheck, color: '#e03131' },
  functionalPerformance: { label: 'Functional Performance', icon: IconTarget, color: '#339af0' },
  fairnessAndEquity: { label: 'Fairness & Equity', icon: IconScale, color: '#7c3aed' },
  dataAndPrivacy: { label: 'Data & Privacy', icon: IconLock, color: '#fd7e14' },
  reliabilityAndContinuity: { label: 'Reliability & Continuity', icon: IconRefresh, color: '#37b24d' },
  humanOversightAndControl: { label: 'Human Oversight & Control', icon: IconUsers, color: '#f59f00' }
};

const progressForPhase = (phase) => {
  switch (phase) {
    case 'discovery': return 25;
    case 'evaluation': return 50;
    case 'actions': return 75;
    case 'residual': return 90;
    case 'completed': return 100;
    default: return 5;
  }
};

export default function NavigatorRiskAutopilot({
  modelId,
  assessmentUuid,
  parentAssessmentUuid,
  businessContext,
  approvedNarrative,
  onComplete,
  onAreaProgress,
  autonomous = false
}) {
  const dispatch = useDispatch();
  const { user } = useAuth0();

  const resumeFromPosition = useSelector((state) => selectResumeData(state, assessmentUuid));

  const assessmentEntry = useSelector((s) => assessmentUuid ? s.assessments?.byId?.[assessmentUuid] : null);
  const contextData = useSelector(selectContextBridge(assessmentUuid, modelId));
  const riskCauseEvalCfg = useSelector((s) => s.config?.configs?.riskCauseEvaluation);

  const inheritedRisks = useSelector((state) => {
    if (!parentAssessmentUuid) return [];
    return selectInheritedBuilderRisks(state, parentAssessmentUuid);
  });

  const impactCategoriesSelector = useMemo(() => createImpactCategoriesSelector(assessmentUuid), [assessmentUuid]);
  const impactCategories = useSelector(impactCategoriesSelector);

  const templateConfig = useMemo(() => assessmentEntry?.templateConfig || {}, [assessmentEntry?.templateConfig]);

  const autonomousRef = useRef(autonomous);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    autonomousRef.current = autonomous;
  }, [autonomous]);

  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [statusByArea, setStatusByArea] = useState({});
  const [areaOutputs, setAreaOutputs] = useState({});
  const [error, setError] = useState(null);
  const [waitingForDecision, setWaitingForDecision] = useState(false);
  const [completedAreaData, setCompletedAreaData] = useState(null);

  // Navigator uses 6 use-case areas - load from template with fallback
  const areas = useMemo(() => {
    const templateAreas = templateConfig?.taxonomy?.riskAreas;
    if (templateAreas && typeof templateAreas === 'object') {
      const templateAreaKeys = Object.keys(templateAreas);
      if (templateAreaKeys.length > 0) return templateAreaKeys;
    }
    return NAVIGATOR_RISK_AREAS;
  }, [templateConfig]);

  const handleLiveUpdate = useCallback((updateType, data) => {
    console.log(`[NavigatorLiveUpdate] ${updateType}:`, data);
    const areaKey = data.areaKey || areas[currentAreaIndex];

    setAreaOutputs((prev) => {
      const currentResults = prev[areaKey] || {};

      switch (updateType) {
        case 'area':
          return { ...prev, [areaKey]: { ...currentResults, areaCard: data.areaCard } };
        case 'sources':
          return { ...prev, [areaKey]: { ...currentResults, selectedSources: data.selectedSources || [] } };
        case 'causes':
          return { ...prev, [areaKey]: { ...currentResults, causesBySource: data.causesBySource || {} } };
        case 'causesForRAG':
          return { ...prev, [areaKey]: { ...currentResults, relevantCauses: data.relevantCauses || [] } };
        case 'scenarios':
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              scenarios: data.scenarios || [],
              ragMetadata: data.ragMetadata || {},
              scenarioStats: {
                totalGenerated: data.totalGenerated || 0,
                totalPromoted: data.totalPromoted || 0,
                generationMethod: data.ragMetadata?.method || 'Unknown'
              }
            }
          };
        case 'riskEvaluation': {
          const evaluations = currentResults.riskEvaluations || [];
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              riskEvaluations: [...evaluations, data],
              evaluationProgress: `${data.evaluatedCount}/${data.totalRisks}`
            }
          };
        }
        case 'controlsGenerated': {
          const controls = currentResults.controlUpdates || [];
          return { ...prev, [areaKey]: { ...currentResults, controlUpdates: [...controls, data] } };
        }
        case 'residualRiskCalculated': {
          const residuals = currentResults.residualCalculations || [];
          return {
            ...prev,
            [areaKey]: {
              ...currentResults,
              residualCalculations: [...residuals, data],
              residualProgress: `${data.assessedCount}/${data.totalRisks}`
            }
          };
        }
        default:
          return prev;
      }
    });
  }, [areas, currentAreaIndex]);

  // Initialize services
  const discoveryService = useMemo(() => new NavigatorDiscoveryService(
    dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, parentAssessmentUuid, businessContext, user, handleLiveUpdate]);

  const evaluationService = useMemo(() => new NavigatorEvaluationService(
    dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate]);

  const actionService = useMemo(() => new NavigatorActionPlanningService(
    dispatch, modelId, assessmentUuid, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);

  const residualService = useMemo(() => new NavigatorResidualRiskService(
    dispatch, modelId, assessmentUuid, handleLiveUpdate
  ), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);

  // Initialize queue
  useEffect(() => {
    const initKey = `${areas.join(',')}-${resumeFromPosition?.pausedAt || 'new'}`;
    if (hasInitializedRef.current === initKey || !areas.length) return;

    hasInitializedRef.current = initKey;

    setStatusByArea((prev) => {
      const next = { ...prev };
      areas.forEach((area, index) => {
        if (!next[area]) {
          next[area] = {
            status: index === (resumeFromPosition?.currentAreaIndex ?? 0) ? 'queued' : 'waiting',
            currentTask: null,
            completedTasks: [],
            startedAt: null,
            completedAt: null
          };
        }
      });
      return next;
    });

    if (resumeFromPosition?.currentAreaIndex >= 0) {
      dispatch(markResumed({ assessmentUuid }));
      setCurrentAreaIndex(resumeFromPosition.currentAreaIndex);
    } else {
      setCurrentAreaIndex(0);
    }
  }, [areas, resumeFromPosition, dispatch, assessmentUuid]);

  const riskNarrative = approvedNarrative || '';

  // Save checkpoint
  const saveProgressCheckpoint = useCallback(async (areaKey, areaIndex, phase = null) => {
    try {
      const completedAreas = areas.slice(0, areaIndex).filter(a =>
        statusByArea[a]?.status === 'completed'
      );

      const checkpoint = {
        majorStep: 'navigator-risks',
        currentArea: areaKey,
        currentAreaIndex: areaIndex,
        completedAreas,
        phase,
        narrativesApproved: true,
        assessmentType: 'navigator',
        pausedAt: new Date().toISOString()
      };

      dispatch(saveCheckpoint({ assessmentUuid, checkpoint }));
      await dispatch(setAssessmentCheckpoint({ modelId, assessmentUuid, checkpoint })).unwrap();

      const status = phase === 'completed' ? 'COMPLETE' : 'IN_PROGRESS';
      const progress = progressForPhase(phase);
      dispatch(setAreaStatus({ assessmentUuid, areaName: areaKey, status }));
      dispatch(updateAreaProgress({ assessmentUuid, areaName: areaKey, progress }));

      console.log(`[NavigatorRiskAutopilot] Checkpoint saved: ${areaKey} (${phase || 'start'})`);
    } catch (checkpointError) {
      console.error('[NavigatorRiskAutopilot] Failed to save checkpoint:', checkpointError);
      setError('Could not persist your progress. Please check your connection.');
    }
  }, [dispatch, modelId, assessmentUuid, areas, statusByArea]);

  // Runner effect
  useEffect(() => {
    const anyGenerating = Object.values(statusByArea).some((s) => s.status === 'generating');
    if (anyGenerating || waitingForDecision) return;

    const currentArea = areas[currentAreaIndex];
    if (!currentArea) {
      dispatch(clearCheckpoint({ assessmentUuid }));
      if (typeof onComplete === 'function') {
        onComplete();
      }
      return;
    }

    if (typeof onAreaProgress === 'function') {
      const completedCount = areas.filter(a => statusByArea[a]?.status === 'completed').length;
      onAreaProgress({
        currentArea: NAVIGATOR_AREA_LABELS[currentArea]?.label || currentArea,
        currentIndex: currentAreaIndex + 1,
        total: areas.length,
        completed: completedCount
      });
    }

    const areaStatus = statusByArea[currentArea]?.status;
    if (areaStatus === 'queued') {
      runAreaWithServices(currentArea);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusByArea, areas, currentAreaIndex, waitingForDecision]);

  async function runAreaWithServices(areaKey) {
    try {
      setError(null);

      setStatusByArea((prev) => ({
        ...prev,
        [areaKey]: {
          ...prev[areaKey],
          status: 'generating',
          currentTask: 'analyzing risk area',
          startedAt: new Date().toISOString()
        }
      }));

      const areaDefinition = templateConfig?.taxonomy?.riskAreas?.[areaKey] || {};

      console.log(`[NavigatorRiskAutopilot] Starting ${areaKey} with full service execution...`);

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'discovery');

      // Phase 1: Discovery
      let lastTask = null;
      const discoveryResults = await discoveryService.executeDiscovery(
        areaKey,
        areaDefinition,
        { riskNarrative, businessContext },
        riskCauseEvalCfg,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[NavigatorDiscovery] Results:`, discoveryResults);

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'evaluation');

      // Phase 2: Evaluation
      const evaluationResults = await evaluationService.executeEvaluation(
        areaKey,
        areaDefinition,
        templateConfig?.taxonomy,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[NavigatorEvaluation] Results:`, evaluationResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], evaluations: evaluationResults }
      }));

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'actions');

      // Phase 3: Action Planning
      const actionResults = await actionService.executeActionPlanning(
        areaKey,
        areaDefinition,
        contextData,
        templateConfig,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[NavigatorActions] Results:`, actionResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], actions: actionResults }
      }));

      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'residual');

      // Phase 4: Residual Risk
      const residualResults = await residualService.executeResidualAssessment(
        areaKey,
        areaDefinition,
        inheritedRisks,
        (currentTask) => {
          updateTaskProgress(areaKey, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[NavigatorResidual] Results:`, residualResults);

      setAreaOutputs((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], residual: residualResults }
      }));

      updateTaskProgress(areaKey, 'residual', null, true);
      await saveProgressCheckpoint(areaKey, currentAreaIndex, 'completed');

      dispatch(fetchRiskCardData({ assessmentUuid, modelId }));

      const finalResults = {
        ...discoveryResults,
        evaluations: evaluationResults,
        actions: actionResults,
        residual: residualResults
      };
      setCompletedAreaData({
        areaKey,
        areaLabel: NAVIGATOR_AREA_LABELS[areaKey]?.label || areaKey,
        results: finalResults,
        isLastArea: currentAreaIndex === areas.length - 1
      });

      if (autonomousRef.current) {
        console.log(`[NavigatorRiskAutopilot] AUTONOMOUS: auto-continuing`);
        setWaitingForDecision(false);
        handleContinueToNext();
      } else {
        console.log(`[NavigatorRiskAutopilot] SUPERVISED: waiting for user`);
        setWaitingForDecision(true);
      }

    } catch (e) {
      setError(e.message);
      setStatusByArea((prev) => ({
        ...prev,
        [areaKey]: { ...prev[areaKey], status: 'failed' }
      }));
    }
  }

  function updateTaskProgress(areaKey, currentTask, nextTask, completed = false) {
    setStatusByArea((prev) => {
      const areaStatus = prev[areaKey] || {};
      const completedTasks = [...(areaStatus.completedTasks || [])];

      if (currentTask && !completedTasks.includes(currentTask)) {
        completedTasks.push(currentTask);
      }

      return {
        ...prev,
        [areaKey]: {
          ...areaStatus,
          currentTask: completed ? null : nextTask,
          completedTasks,
          status: completed ? 'completed' : 'generating',
          completedAt: completed ? new Date().toISOString() : areaStatus.completedAt
        }
      };
    });
  }

  const handleContinueToNext = useCallback(() => {
    setWaitingForDecision(false);
    setCompletedAreaData(null);

    if (currentAreaIndex + 1 < areas.length) {
      const nextAreaIndex = currentAreaIndex + 1;
      const nextArea = areas[nextAreaIndex];

      setCurrentAreaIndex(nextAreaIndex);
      setStatusByArea(prev => ({
        ...prev,
        [nextArea]: { ...prev[nextArea], status: 'queued' }
      }));
    } else {
      console.log(`[NavigatorRiskAutopilot] All 6 Navigator areas complete`);
      if (typeof onComplete === 'function') {
        onComplete();
      }
    }
  }, [currentAreaIndex, areas, onComplete]);

  const handleSkipArea = useCallback((areaToSkip) => {
    setStatusByArea(prev => ({
      ...prev,
      [areaToSkip]: { ...prev[areaToSkip], status: 'skipped' }
    }));
    handleContinueToNext();
  }, [handleContinueToNext]);

  const handleGenerateReport = () => {
    setWaitingForDecision(false);
    setCompletedAreaData(null);
    if (typeof onComplete === 'function') {
      onComplete();
    }
  };

  const getTaskProgress = (areaKey) => {
    const areaStatus = statusByArea[areaKey] || {};
    const completedCount = areaStatus.completedTasks?.length || 0;
    return {
      completed: completedCount,
      total: NAVIGATOR_TASKS.length,
      percentage: Math.round((completedCount / NAVIGATOR_TASKS.length) * 100)
    };
  };

  const currentArea = areas[currentAreaIndex];
  const currentAreaLabel = NAVIGATOR_AREA_LABELS[currentArea]?.label || currentArea;

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertTriangle size="1rem" />}>
          {error}
        </Alert>
      )}

      <Transition mounted={!!currentArea} transition="fade" duration={400} timingFunction="ease-out">
        {(styles) => currentArea && (
          <div style={styles}>
            <NavigatorAreaCard
              areaKey={currentArea}
              areaLabel={currentAreaLabel}
              areaIndex={currentAreaIndex + 1}
              totalAreas={areas.length}
              status={statusByArea[currentArea] || {}}
              outputs={areaOutputs[currentArea]}
              progress={getTaskProgress(currentArea)}
              autonomous={autonomous}
              inheritedRisksCount={inheritedRisks.length}
              onRetry={() => setStatusByArea(prev => ({
                ...prev,
                [currentArea]: { ...prev[currentArea], status: 'queued' }
              }))}
              isWaitingForUserDecision={waitingForDecision}
              completedAreaData={completedAreaData}
              remainingAreas={areas.slice(currentAreaIndex + 1).map(a => NAVIGATOR_AREA_LABELS[a]?.label || a)}
              onContinue={handleContinueToNext}
              onSkip={() => handleSkipArea(currentArea)}
              onGenerateReport={handleGenerateReport}
            />
          </div>
        )}
      </Transition>
    </Stack>
  );
}

/**
 * Navigator Area Card - Shows use-case risk area processing progress
 */
function NavigatorAreaCard({
  areaKey,
  areaLabel,
  areaIndex,
  totalAreas,
  status,
  outputs,
  progress,
  autonomous,
  inheritedRisksCount,
  onRetry,
  isWaitingForUserDecision,
  completedAreaData,
  remainingAreas,
  onContinue,
  onSkip,
  onGenerateReport
}) {
  const isGenerating = status.status === 'generating';
  const isCompleted = status.status === 'completed';
  const isFailed = status.status === 'failed';
  const isQueued = status.status === 'queued';

  const areaConfig = NAVIGATOR_AREA_LABELS[areaKey] || { icon: IconAnalyze, color: '#fd7e14' };
  const AreaIcon = areaConfig.icon;

  return (
    <Card
      withBorder
      p="lg"
      radius="md"
      style={{
        height: '100%',
        transition: 'all 0.2s ease',
        transform: isGenerating ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isGenerating ? '0 8px 25px rgba(253, 126, 20, 0.15)' : undefined,
        borderLeft: `4px solid ${areaConfig.color}`
      }}
    >
      <Stack gap="md" h="100%">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" style={{ flex: 1 }} align="flex-start">
            <AreaIcon size={24} color={areaConfig.color} />
            <div style={{ flex: 1 }}>
              <Group gap="xs" align="baseline">
                <Text fw={700} size="lg" lineClamp={2}>
                  {areaLabel}
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  ({areaIndex}/{totalAreas})
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                {isQueued && 'Waiting in queue...'}
                {isGenerating && 'Huginn is analyzing risks...'}
                {isCompleted && 'Risk analysis complete'}
                {isFailed && 'Analysis failed'}
              </Text>
              {inheritedRisksCount > 0 && (
                <Badge size="xs" variant="light" color="orange" mt={4}>
                  {inheritedRisksCount} inherited Builder risks
                </Badge>
              )}
            </div>
          </Group>

          <Badge
            variant="light"
            color={isCompleted ? 'green' : isGenerating ? 'orange' : isFailed ? 'red' : 'gray'}
            size="sm"
          >
            {isCompleted && <IconCheck size={14} />}
            {isGenerating && 'Processing'}
            {isFailed && <IconAlertTriangle size={14} />}
            {isQueued && <IconHourglassHigh size={14} />}
          </Badge>
        </Group>

        {/* Progress Ring */}
        <Center>
          <RingProgress
            size={120}
            thickness={8}
            sections={[
              {
                value: progress.percentage,
                color: isCompleted ? '#40c057' : isGenerating ? areaConfig.color : '#adb5bd'
              }
            ]}
            label={
              <Center>
                {isCompleted ? (
                  <IconCheck size={32} color="#40c057" />
                ) : isGenerating ? (
                  <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={28} />
                ) : (
                  <AreaIcon size={28} color="#adb5bd" />
                )}
              </Center>
            }
          />
        </Center>

        {/* Task Timeline */}
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={500} mb="xs">
            Risk Analysis Progress
          </Text>
          <Timeline active={progress.completed} bulletSize={24} lineWidth={2}>
            {NAVIGATOR_TASKS.map((task) => {
              const isActive = status.currentTask === task.key;
              const isTaskCompleted = status.completedTasks?.includes(task.key);

              return (
                <Timeline.Item
                  key={task.key}
                  bullet={
                    <Center style={{ width: 24, height: 24 }}>
                      {isTaskCompleted ? (
                        <IconCheck size={16} color="#40c057" />
                      ) : isActive ? (
                        <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={14} />
                      ) : (
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: '#adb5bd'
                          }}
                        />
                      )}
                    </Center>
                  }
                  title={
                    <Text
                      size="xs"
                      fw={isActive ? 600 : 400}
                      c={isTaskCompleted ? 'green' : isActive ? 'orange' : 'dimmed'}
                    >
                      {task.label}
                    </Text>
                  }
                >
                  <Text size="xs" c="dimmed">
                    {task.description}
                  </Text>
                  {isActive && (
                    <Box p="xs" bg="orange.0" style={{ borderRadius: 4 }} mt="xs">
                      <Group gap="xs" align="center">
                        <AIGeneratingIcon isGenerating size={12} />
                        <Text size="xs" c="orange">
                          Huginn is working on this task...
                        </Text>
                      </Group>
                    </Box>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Box>

        {/* Completion Summary */}
        {isCompleted && outputs && (
          <Box>
            <Divider my="sm" />
            <Text size="sm" fw={600} c="green" mb="xs">
              Risk Analysis Complete
            </Text>
            <Group gap="xs" mb="md">
              {outputs.scenarios?.length > 0 && (
                <Badge size="xs" variant="light" color="purple">
                  {outputs.scenarios.length} scenarios
                </Badge>
              )}
              {outputs.evaluations?.evaluatedRisks > 0 && (
                <Badge size="xs" variant="light" color="grape">
                  {outputs.evaluations.evaluatedRisks} priorities
                </Badge>
              )}
              {outputs.actions?.actionsGenerated > 0 && (
                <Badge size="xs" variant="light" color="orange">
                  {outputs.actions.actionsGenerated} controls
                </Badge>
              )}
              {outputs.residual?.residualAssessments > 0 && (
                <Badge size="xs" variant="light" color="teal">
                  {outputs.residual.residualAssessments} residual
                </Badge>
              )}
            </Group>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="center" gap="sm" mt="md">
          {isGenerating && (
            <Button
              size="sm"
              variant="light"
              color="orange"
              onClick={onSkip}
            >
              Skip Area
            </Button>
          )}

          {isCompleted && (
            <>
              <Text size="sm" fw={500} c="green" mr="md">
                Analysis Complete!
              </Text>

              {autonomous ? (
                <Group gap="xs" align="center">
                  <IconHourglassHigh size={16} color="#fd7e14" />
                  <Text size="sm" c="orange">
                    {remainingAreas && remainingAreas.length > 0
                      ? `Continuing to ${remainingAreas[0]}...`
                      : 'Navigator assessment complete!'}
                  </Text>
                </Group>
              ) : (
                isWaitingForUserDecision && (
                  <>
                    {remainingAreas && remainingAreas.length > 0 ? (
                      <Button
                        size="md"
                        color="orange"
                        leftSection={<IconChevronRight size={18} />}
                        onClick={onContinue}
                      >
                        Continue to {remainingAreas[0]}
                      </Button>
                    ) : (
                      <Button
                        size="md"
                        color="orange"
                        leftSection={<IconCheck size={18} />}
                        onClick={onGenerateReport}
                      >
                        Generate Navigator Report
                      </Button>
                    )}
                  </>
                )
              )}
            </>
          )}

          {isFailed && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                leftSection={<IconRefresh size={14} />}
              >
                Retry Analysis
              </Button>
              <Button
                size="sm"
                variant="light"
                color="orange"
                onClick={onSkip}
              >
                Skip Area
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
